import { useTranslation } from 'react-i18next';

import { PasswordInput, Stack } from '@mantine/core';
import {
  hasLength,
  isNotEmpty,
  matches,
  matchesField,
  type UseFormReturnType,
} from '@mantine/form';
import { type TFunction } from 'i18next';

import type { InterceptedError } from '@/shared/api/intercepted-error';

type FormState = {
  old_password: string;
  new_password: string;
  confirm_new_password: string;
};

type PasswordFormField = keyof FormState;

const passwordFormFields = new Set<PasswordFormField>([
  'old_password',
  'new_password',
  'confirm_new_password',
]);

const isPasswordFormField = (value: unknown): value is PasswordFormField =>
  typeof value === 'string' &&
  passwordFormFields.has(value as PasswordFormField);

export const setPasswordFormApiErrors = (
  form: UseFormReturnType<FormState>,
  error: InterceptedError,
  fallbackField: PasswordFormField,
) => {
  const invalidParams = error.response?.data?.invalid_params;
  let hasFieldError = false;

  if (Array.isArray(invalidParams)) {
    invalidParams.forEach((invalidParam) => {
      const field = invalidParam.loc?.at(-1);

      if (!isPasswordFormField(field)) return;

      form.setFieldError(field, invalidParam.msg);
      hasFieldError = true;
    });
  }

  if (!hasFieldError) {
    form.setFieldError(fallbackField, error.message);
  }
};

export type ChangePasswordFormHandle = {
  form: () => UseFormReturnType<FormState>;
};

export const passwordFormConfig = ({
  t,
  oldPassword = true,
}: {
  t: TFunction;
  oldPassword?: boolean;
}) => ({
  initialValues: {
    old_password: '',
    new_password: '',
    confirm_new_password: '',
  },
  validateInputOnChange: true,
  validateInputOnBlur: true,
  clearInputErrorOnChange: true,
  validate: {
    old_password: (val) =>
      oldPassword ? isNotEmpty(t('required', { ns: 'validation' }))(val) : null,
    new_password: (val) =>
      isNotEmpty(t('required', { ns: 'validation' }))(val) ||
      matches(
        /(?=.*[!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~;])+/g,
        t('password.oneSymbol', { ns: 'validation' }),
      )(val) ||
      matches(
        /(?=.*[a-z])+/g,
        t('password.oneLowerCase', { ns: 'validation' }),
      )(val) ||
      matches(
        /(?=.*[0-9])+/g,
        t('password.oneNumber', { ns: 'validation' }),
      )(val) ||
      matches(
        /^[0-9a-zA-Z!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~;]{8,}$/g,
        t('password.minSymbols', { ns: 'validation' }),
      )(val) ||
      hasLength(
        { max: 255 },
        t('password.maxSymbols', { ns: 'validation' }),
      )(val),

    confirm_new_password: (val, values) =>
      isNotEmpty(t('required', { ns: 'validation' }))(val) ||
      matchesField(
        'new_password',
        t('password.notEqual', { ns: 'validation' }),
      )(val, values),
  },
});
export const ChangePasswordForm = ({
  form,
  oldPassword = true,
}: {
  form: UseFormReturnType<FormState>;
  oldPassword?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Stack>
      {oldPassword && (
        <PasswordInput
          data-testid={'change-password-old'}
          required
          label={t('passwordDialog.oldPassword', { ns: 'users' })}
          {...form.getInputProps('old_password')}
        />
      )}
      <PasswordInput
        data-testid={'change-password-new'}
        errorProps={{ 'data-testid': 'change-password-new-error' }}
        required
        label={t('passwordDialog.newPassword', { ns: 'users' })}
        {...form.getInputProps('new_password')}
      />
      <PasswordInput
        data-testid={'change-password-repeat'}
        required
        label={t('passwordDialog.rePassword', { ns: 'users' })}
        {...form.getInputProps('confirm_new_password')}
      />
    </Stack>
  );
};
